import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { inlineErrorClass } from '@/components/ui/inputStyles';

type RobotOption = {
  id: number;
  robot_name: string;
  enabled: boolean;
  miniprogram_id: number;
  miniprogram_name: string;
  selected: boolean;
};

type Props = {
  open: boolean;
  areaId: number;
  areaName: string;
  onClose: () => void;
  onSaved?: () => void;
};

export function PromotionAreaRobotsModal({ open, areaId, areaName, onClose, onSaved }: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [items, setItems] = useState<RobotOption[]>([]);
  const [sel, setSel] = useState<Record<number, boolean>>({});

  const load = useCallback(async () => {
    if (!open || areaId < 1) return;
    setLoading(true);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; items: RobotOption[]; err?: string }>('list_robots_for_promotion_area', {
        method: 'GET',
        query: { area_id: areaId },
      });
      if (!r.ok) {
        setErr('加载机器人列表失败');
        setItems([]);
        return;
      }
      setItems(r.items);
      const m: Record<number, boolean> = {};
      for (const it of r.items) {
        if (it.selected) m[it.id] = true;
      }
      setSel(m);
    } finally {
      setLoading(false);
    }
  }, [open, areaId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    setSaving(true);
    setErr('');
    try {
      const robotIds = Object.entries(sel)
        .filter(([, v]) => v)
        .map(([k]) => parseInt(k, 10))
        .filter((x) => x > 0);
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_promotion_area_robots', {
        method: 'POST',
        body: JSON.stringify({ area_id: areaId, robot_ids: robotIds }),
      });
      if (!r.ok) {
        setErr('保存失败');
        return;
      }
      onSaved?.();
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} title={`配置机器人 · ${areaName}`} onClose={() => !saving && onClose()} wide>
      <p className="text-sm leading-relaxed text-slate-600">
        选择与该推广区域绑定的 Android 转发机器人（可多选）。绑定关系与「机器人管理」中的微推广配置相互同步。
      </p>
      {err ? <p className={`mt-2 ${inlineErrorClass}`}>{err}</p> : null}
      <div className="mt-4 max-h-[min(24rem,50vh)] overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/50 p-2">
        {loading ? (
          <Spinner label="加载机器人" />
        ) : items.length === 0 ? (
          <p className="px-2 py-4 text-sm text-slate-500">暂无可用机器人，请先在「机器人管理」中创建并启用。</p>
        ) : (
          <ul className="space-y-1">
            {items.map((it) => (
              <li key={it.id}>
                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                    sel[it.id]
                      ? 'border-teal-300 bg-white shadow-sm'
                      : 'border-transparent bg-white/80 hover:border-slate-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 shrink-0"
                    checked={!!sel[it.id]}
                    onChange={(e) => setSel((m) => ({ ...m, [it.id]: e.target.checked }))}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="font-medium text-slate-900">{it.robot_name}</span>
                    <span className="ml-2 font-mono text-[11px] text-slate-500">#{it.id}</span>
                    {!it.enabled ? (
                      <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                        机器人已停用
                      </span>
                    ) : null}
                    {it.miniprogram_name ? (
                      <span className="mt-0.5 block text-xs text-slate-500">小程序：{it.miniprogram_name}</span>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <Button type="button" variant="ghost" disabled={saving} onClick={onClose}>
          取消
        </Button>
        <Button type="button" variant="primary" disabled={saving || loading} onClick={() => void save()}>
          {saving ? '保存中…' : '保存绑定'}
        </Button>
      </div>
    </Modal>
  );
}
